import React from 'react';
import { Link } from 'react-router-dom';
import PublicPageLayout from '../components/landing-page/PublicPageLayout';
import { CAREER_JOBS } from './careersData';

function JobCard({
  id,
  title,
  team,
  location,
  summary,
}: {
  id: string;
  title: string;
  team: string;
  location: string;
  summary: string;
}) {
  return (
    <Link
      to={`/careers/jobs/${id}`}
      className='group block rounded-xl border border-gray-200 bg-white p-5 text-gray-900 shadow-sm ring-1 ring-gray-900/5 transition hover:border-yellow-500/60 hover:shadow-md dark:border-gray-200 dark:bg-white dark:text-gray-900 dark:hover:border-yellow-500/60'
    >
      <div className='flex items-start justify-between gap-3'>
        <div>
          <h3 className='text-base font-semibold text-gray-900 group-hover:text-yellow-600 dark:text-gray-900 dark:group-hover:text-yellow-600'>
            {title}
          </h3>
          <p className='mt-1 text-sm text-gray-500 dark:text-gray-600'>
            {team} · {location}
          </p>
        </div>
        <span
          className='shrink-0 text-gray-400 transition group-hover:translate-x-0.5 group-hover:text-yellow-600 dark:text-gray-400 dark:group-hover:text-yellow-600'
          aria-hidden
        >
          →
        </span>
      </div>
      <p className='mt-3 text-sm leading-relaxed text-gray-600 dark:text-gray-700'>{summary}</p>
    </Link>
  );
}

export default function CareersPage() {
  return (
    <PublicPageLayout
      title='Careers'
      subtitle=''
      contentMaxWidthClassName='max-w-7xl'
    >
      <div className='grid gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(280px,380px)] lg:items-start'>
        <div className='space-y-10'>
          <div className='rounded-2xl border border-gray-200 bg-gray-50 p-8 text-gray-900 shadow-sm ring-1 ring-gray-900/5'>
            <h2 className='text-lg font-semibold text-gray-900'>Why join us</h2>
            <p className='mt-3 text-gray-700'>
              We are building category-defining tooling at the intersection of pricing, growth, and machine learning.
              Small team, high ownership, and direct lines from customer feedback to what ships next.
              <br />
              <br />
              We are an all remote company. You may live anywhere you like as long as you can work Pacific Time Zone.
              <br />
              <br />
              As a company we highly value mentorship and provide educational time and instructional attention to grow individually and as a team. 
            </p>
          </div>

          <div>
            <h2 className='text-lg font-semibold text-gray-900 dark:text-white'>Principles</h2>
            <ul className='mt-4 list-disc space-y-2 pl-5 text-gray-700 dark:text-gray-300'>
              <li>Build Something Great-<br />
              We’re here to create products that genuinely help people—not just ship features. Every line of code, every design, every customer interaction moves us closer to something meaningful. We value craft, clarity, and the long view over quick wins.</li>
              <li>Max Agency-<br />
              We hire for trust and autonomy. Everyone here owns their projects end to end, from idea to deployment. No endless approvals or red tape—if you see a way to make something better, you have the freedom (and support) to do it.</li>
              <li>Career Building-<br />
              Joining us means joining a team that invests in your growth. You’ll work across disciplines, ship real impact, and learn directly from experienced founders and teammates. When you grow, the company does too.</li>
            </ul>
          </div>

          <div>
            <h2 className='text-lg font-semibold text-gray-900 dark:text-white'>Hiring Process</h2>
            <ul className='mt-4 list-disc space-y-2 pl-5 text-gray-700 dark:text-gray-300'>
              <li>Talent When Found-<br />
              John D. Rockefeller once said he hired{' '}
              <em>&ldquo;men when found, not when needed.&rdquo;</em> We live by that same idea. When exceptional talent crosses our path, we make room—no matter the timeline. Great people shape opportunities as much as they fill them, and we’d rather move early than miss the chance to work with someone remarkable.</li>
              <li>Our Response-<br />
              We respect the time and energy you invest in applying. That means clear communication, fast feedback, and no vanishing acts. Every candidate deserves to know where they stand, and we treat the process with the same professionalism we value in our work.</li>
              <li>One Stage-<br />
              Our interview process is lean by design—just one focused conversation with the right people. You’ll meet the founders or core team, discuss real challenges we’re tackling, and see if our ways of thinking align. If it’s a fit, we move quickly.</li>
            </ul>
          </div>
        </div>

        <aside className='lg:sticky lg:top-28'>
          <div className='mb-4 flex items-baseline justify-between gap-2'>
            <h2 className='text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400'>
              Open positions
            </h2>
            <span className='text-sm text-gray-400 dark:text-gray-500'>{CAREER_JOBS.length}</span>
          </div>
          <div className='space-y-4'>
            {CAREER_JOBS.map((job) => (
              <JobCard
                key={job.id}
                id={job.id}
                title={job.title}
                team={job.team}
                location={job.location}
                summary={job.summary}
              />
            ))}
          </div>
          <p className='mt-6 text-xs leading-relaxed text-gray-500 dark:text-gray-400'>
            We use structured application flows so every candidate gets a consistent experience. Select a role to read
            the full description and apply.
          </p>
        </aside>
      </div>
    </PublicPageLayout>
  );
}
